// Dependencies
/* tslint:disable */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';

// Components
import { Dialog } from '../../Dialog';

// Styles
import styles, { containerSxByMode, modalSxByMode, PreviewViewMode } from './styles';
import { CDN_GENERATOR_PATH } from '../../../contants';

interface IWidgetPreviewDialog {
  open: boolean;
  selectedWidget: any | null;
  onClose: () => void;
}

function buildPreviewSrcDoc(widgetId: string) {
  const safeId = String(widgetId).replace(/[^a-zA-Z0-9_-]/g, '');
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      min-height: 100%;
      background: #fff;
    }
    .cdn-root {
      width: 100%;
      max-width: 100%;
      overflow-x: hidden;
    }
  </style>
</head>
<body>
  <div class="cdn-root" widgetId="${safeId}" page="factsheet"></div>
  <script src="${CDN_GENERATOR_PATH}"></script>
</body>
</html>`;
}

function dialogSxForMode(mode: PreviewViewMode) {
  if (mode === 'fullscreen') {
    return styles.dialogFullscreen;
  }
  if (mode === 'mobile') {
    return styles.dialogMobile;
  }
  return styles.dialog;
}

// Export ESG Geschäftsfelder doc dialog
export const WidgetPreviewDialog: FC<IWidgetPreviewDialog> = ({ open, selectedWidget, onClose }) => {
  const { _id: widgetId } = selectedWidget;
  const [viewMode, setViewMode] = useState<PreviewViewMode>('default');
  const [frameKey, setFrameKey] = useState(0);

  useEffect(() => {
    if (!open) {
      setViewMode('default');
    }
  }, [open]);

  // Remount iframe when mode changes so media queries use the new viewport width.
  useEffect(() => {
    if (!open) {
      return;
    }
    const timer = window.setTimeout(() => {
      setFrameKey((key) => key + 1);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [viewMode, open, widgetId]);

  const srcDoc = useMemo(() => buildPreviewSrcDoc(widgetId), [widgetId, frameKey]);

  const toggleFullscreen = () => {
    setViewMode((prev) => (prev === 'fullscreen' ? 'default' : 'fullscreen'));
  };

  const toggleMobile = () => {
    setViewMode((prev) => (prev === 'mobile' ? 'default' : 'mobile'));
  };

  const headerActions = (
    <>
      <Tooltip title={viewMode === 'fullscreen' ? 'Exit full screen' : 'Full screen'}>
        <IconButton
          size="small"
          color="secondary"
          className={viewMode === 'fullscreen' ? 'active' : undefined}
          sx={styles.headerBtn}
          onClick={toggleFullscreen}
        >
          {viewMode === 'fullscreen' ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Tooltip>
      <Tooltip title={viewMode === 'mobile' ? 'Exit mobile view' : 'Mobile view'}>
        <IconButton
          size="small"
          color="secondary"
          className={viewMode === 'mobile' ? 'active' : undefined}
          sx={styles.headerBtn}
          onClick={toggleMobile}
        >
          <SmartphoneOutlinedIcon sx={styles.phoneIcon} />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={dialogSxForMode(viewMode)}
      headerActions={headerActions}
      modalSx={modalSxByMode[viewMode]}
      containerSx={containerSxByMode[viewMode]}
    >
      <Box sx={styles.cdnWrapper}>
        {open && (
          <Box
            key={`${widgetId}-${frameKey}-${viewMode}`}
            component="iframe"
            title="Widget preview"
            srcDoc={srcDoc}
            sx={styles.iframe}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          />
        )}
      </Box>
    </Dialog>
  );
};
